/* eslint-disable no-useless-escape */
import { AbstractControl, FormControl, FormGroup, ValidationErrors,  ValidatorFn, Validators } from '@angular/forms';


export const ZipCodeValidatorArray = [Validators.required, Validators.maxLength(5)];

/**
 * Get zip code validators based on country
 * @param countryCode - The ISO country code (e.g., 'US', 'CA', etc.)
 * @returns Array of validators for the zip code field
 */
export function getZipCodeValidators(countryCode: string): ValidatorFn[] {
  if (countryCode === 'US') {
    return ZipCodeValidatorArray;
  }
  return [Validators.required];
}

/**
 * Helper function to update address form validations when country changes
 * Handles state, state_text, and zip field validations
 * @param form - The FormGroup containing the address fields
 * @param countryCode - The selected country code
 * @param stateList - Array of states for the selected country
 */
export function updateAddressValidationsByCountry(
  form: FormGroup,
  countryCode: string,
  stateList: any[] = []
): void {
  const stateControl = form.get('state');
  const stateTextControl = form.get('state_text');
  const zipControl = form.get('zip');

  if (!stateControl || !zipControl) {
    return; // Required controls don't exist
  }

  // Update state/state_text validators
  if (countryCode !== 'US') {
    stateControl.clearValidators();
    stateControl.updateValueAndValidity();
    if (stateTextControl) {
      stateTextControl.setValidators([Validators.required]);
      stateTextControl.updateValueAndValidity();
    }
    stateControl.reset();
  } else {
    stateControl.setValidators([Validators.required]);
    stateControl.updateValueAndValidity();
    if (stateTextControl) {
      stateTextControl.clearValidators();
      stateTextControl.updateValueAndValidity();
      stateTextControl.reset();
    }
  }

  // Update zip validators based on country
  zipControl.setValidators(getZipCodeValidators(countryCode));
  zipControl.updateValueAndValidity();
}

const luhnChk = (arr => {
  return ccNum => {
    let len = ccNum.length,
      bit = 1,
      sum = 0,
      val;

    while (len) {
      val = parseInt(ccNum.charAt(--len), 10);
      // eslint-disable-next-line no-cond-assign
      sum += (bit ^= 1) ? arr[val] : val;
    }

    return sum && sum % 10 === 0;
  };
})([0, 2, 4, 6, 8, 1, 3, 5, 7, 9]);

export class CustomValidator {

  static email(control: FormControl) {

    // tslint:disable-next-line:max-line-length
    const EMAIL_REGEXP = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return EMAIL_REGEXP.test(control.value) ? null : {
      validateEmail: {
        valid: false
      }
    };
  }

  static noWhitespace(control: FormControl) {
    const pattern = /^[^\s]+(\s+[^\s]+)*$/;
    return (pattern.test(control.value)) ? null : {
      noWhitespace: {
        valid: false
      }
    };
  }

  static number(control: FormControl) {

    const pattern = /^[0-9]+(\.[0-9]*){0,1}$/g;

    return pattern.test(control.value) ? null : {
      validateNumber: {
        valid: false
      }
    };
  }

  static password(control: FormControl) {

    const pattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{5,20}/;
    return pattern.test(control.value) ? null : {
      invalidPassword: {
        valid: false
      }
    };
  }


  static CCValidator(c: FormControl) {
    return c.value && luhnChk(c.value.toString())
      ? null
      : { invalidCreditCard: true };
  }

  static pricingField(control: FormControl) {

    const pattern = /^[0-9]+(\.[0-9]*){0,1}$/g;

    return (pattern.test(control.value) || control.value === '+') ? null : {
      validatePricingField: {
        valid: false
      }
    };
  }

  static confirmPassword(formGP: FormGroup) {
    const password = formGP.controls.password.value;
    const repeatPassword = formGP.controls.confirm_password.value;

    if (repeatPassword.length <= 0) {
      return null;
    }

    if (repeatPassword !== password) {
      return {
        doesMatchPassword: true
      };
    }

    return null;
  }

  static url(control: FormControl) {

    const pattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;

    return pattern.test(control.value) ? null : {
      url: {
        valid: false
      }
    };
  }


  static checkDateValidator(expiredMonth: string, expiredYear: string) {
    return (group: FormGroup) => {
      const month = group.controls[expiredMonth];
      const year = group.controls[expiredYear];
      const lastDay = new Date(year.value, month.value, 0);
      const expiryDate = new Date(
        year.value + '-' + month.value + '-' + lastDay
      );
      if (year && year.value) {
        expiryDate < new Date()
          ? year.setErrors({ invalidDate: true })
          : year.setErrors(null);
      }
    };
  }

}
export function passwordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    if (!hasLetter || !hasNumber) {
      return { passwordComplexity: { message: 'Must contain letters and numbers.' } };
    }

    if (value.length < 12) {
      return { passwordLength: { message: 'Must be at least 12 characters long.' } };
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    if (!hasUpperCase || !hasLowerCase) {
      return { passwordCase: { message: 'Must contain both upper and lower-case letters.' } };
    }

    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(value);
    if (!hasSpecialChar) {
      return { passwordSpecialChar: { message: 'Must contain at least one special character.' } };
    }

    return null;
  };
}
